import { db } from '../../../shared/storage/firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { User, Squad } from '../../../types';
import { ParentLoginTracker, ParentLoginStats } from './ParentLoginTracker';

export interface LinkedChildData {
  child: User;
  squad: Squad | null;
  teacher: User | null;
}

export interface ParentDashboardData {
  parent: User;
  linkedChildren: LinkedChildData[];
  loginStats: ParentLoginStats;
}

export class ParentService {
  private static describeFsError(error: any): { code?: string; message?: string; name?: string } {
    return {
      code: typeof error?.code === 'string' ? error.code : undefined,
      message: typeof error?.message === 'string' ? error.message : undefined,
      name: typeof error?.name === 'string' ? error.name : undefined,
    };
  }

  private static async getTeacherPublicProfile(teacherId: string): Promise<User | null> {
    const id = String(teacherId || '').trim();
    if (!id) return null;
    try {
      // Parents generally cannot read staff user docs. Use the public teacher profile mirror.
      const teacherDoc = await getDoc(doc(db, 'publicTeachers', id));
      if (!teacherDoc.exists()) return null;
      const data = teacherDoc.data() as any;
      return { id: teacherDoc.id, ...(data || {}) } as any as User;
    } catch (e) {
      console.warn('[ParentService] Could not load public teacher profile', { teacherId: id, e });
      return null;
    }
  }

  private static async getLinkedChildDataFromUser(child: User): Promise<LinkedChildData | null> {
    const childId = String((child as any)?.id || '').trim();
    if (!childId) return null;
    try {
      // Find the squad this child belongs to
      const squadsRef = collection(db, 'squads');
      const squadQuery = query(squadsRef, where('studentIds', 'array-contains', childId));
      const squadSnapshot = await getDocs(squadQuery);

      let squad: Squad | null = null;
      let teacher: User | null = null;

      if (!squadSnapshot.empty) {
        const allSquads = squadSnapshot.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as Squad[];
        // Selection heuristics
        const today = new Date();
        const parseISO = (s?: string) => (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) ? new Date(`${s}T00:00:00`) : (s ? new Date(s) : undefined);
        const hasValidSchedule = (s?: Squad) => !!(s?.schedule?.mainDate || (s?.schedule?.startDate && s?.schedule?.endDate));
        const includesToday = (s?: Squad) => {
          if (!hasValidSchedule(s)) return false;
          const start = parseISO(s!.schedule!.mainDate || s!.schedule!.startDate)!;
          const end = parseISO(s!.schedule!.mainDate || s!.schedule!.endDate)!;
          return start <= today && today <= end;
        };
        const statusScore = (s?: Squad) => s?.classStatus === 'current' ? 2 : s?.classStatus === 'next' ? 1 : 0;

        // Rank squads by: published status, has valid schedule, includes today, classStatus score, has any schedule
        const ranked = [...allSquads].sort((a, b) => {
          const pubA = a.status === 'published' ? 1 : 0;
          const pubB = b.status === 'published' ? 1 : 0;
          if (pubB !== pubA) return pubB - pubA;

          const valA = hasValidSchedule(a) ? 1 : 0;
          const valB = hasValidSchedule(b) ? 1 : 0;
          if (valB !== valA) return valB - valA;

          const incA = includesToday(a) ? 1 : 0;
          const incB = includesToday(b) ? 1 : 0;
          if (incB !== incA) return incB - incA;

          const statA = statusScore(a);
          const statB = statusScore(b);
          if (statB !== statA) return statB - statA;

          const anySchedA = a.schedule ? 1 : 0;
          const anySchedB = b.schedule ? 1 : 0;
          if (anySchedB !== anySchedA) return anySchedB - anySchedA;

          // fallback to createdAt descending if available
          const caA = (a as any).createdAt?.toMillis?.() ?? 0;
          const caB = (b as any).createdAt?.toMillis?.() ?? 0;
          return caB - caA;
        });

        squad = ranked[0] || null;
        console.debug('[ParentService] Found squads for child (selected best)', {
          childId,
          total: allSquads.length,
          selectedSquadId: squad?.id,
          selectedTitle: squad?.title,
          selectedStatus: squad?.status,
          selectedClassStatus: squad?.classStatus,
          selectedSchedule: squad?.schedule
        });

        // Resolve teacher with priority: regularTeacher (canonical) -> leadTeacherId (legacy) -> first teacherIds[0]
        const preferredTeacherId = (squad as any).regularTeacher || (squad as any).leadTeacherId || (squad.teacherIds && squad.teacherIds[0]);
        if (preferredTeacherId) {
          teacher = await this.getTeacherPublicProfile(String(preferredTeacherId));
        }
      }

      const result = {
        child,
        squad,
        teacher
      };
      console.debug('[ParentService] Linked child data', result);
      return result;
    } catch (error) {
      console.error('Error fetching child data:', error);
      return null;
    }
  }

  /**
   * Get comprehensive parent dashboard data including all linked children,
   * their squads, teachers, and program logistics
   */
  static async getParentDashboardData(parentId: string): Promise<ParentDashboardData | null> {
    try {
      // Get parent user data
      let parentDoc;
      try {
        parentDoc = await getDoc(doc(db, 'users', parentId));
      } catch (e) {
        console.error('[ParentService] Failed to load parent user doc', {
          parentId,
          error: this.describeFsError(e),
        });
        throw e;
      }
      if (!parentDoc.exists()) {
        console.error('Parent not found:', parentId);
        return null;
      }

      const parent = { id: parentDoc.id, ...parentDoc.data() } as User;
      const linkedChildren: LinkedChildData[] = [];

      // Canonical source of truth: children that link back via `parentIds`.
      // This avoids permission errors when `parent.studentIds` contains stale/deleted IDs
      // or IDs that never got the reverse-link field set.
      let children: User[] = [];
      let childrenSource: 'parentIds_query' | 'fallback_studentIds' = 'parentIds_query';
      try {
        const childrenSnap = await getDocs(query(collection(db, 'users'), where('parentIds', 'array-contains', parentId)));
        children = childrenSnap.docs
          .map(d => ({ id: d.id, ...(d.data() as any) } as User))
          // Defensive: only show student-like child accounts on the dashboard
          .filter(u => String((u as any)?.role || '') === 'student')
          .sort((a, b) => String(a.displayName || '').localeCompare(String(b.displayName || '')));
      } catch (e) {
        console.error('[ParentService] Failed to query children by parentIds (will fall back to parent.studentIds)', {
          parentId,
          error: this.describeFsError(e),
        });
        childrenSource = 'fallback_studentIds';
        const ids = Array.isArray((parent as any).studentIds) ? ((parent as any).studentIds as any[]).map(String) : [];
        const uniq = Array.from(new Set(ids.filter(Boolean)));
        for (const childId of uniq) {
          const childData = await this.getLinkedChildData(childId);
          if (childData?.child) children.push(childData.child);
        }
        // Keep stable ordering
        children = children
          .filter(u => String((u as any)?.role || '') === 'student')
          .sort((a, b) => String(a.displayName || '').localeCompare(String(b.displayName || '')));
      }

      console.debug('[ParentService] Loaded parent user', {
        parentId,
        studentIds: parent.studentIds,
        resolvedChildren: children.map(c => c.id),
        childrenSource,
      });

      for (const child of children) {
        const childData = await this.getLinkedChildDataFromUser(child);
        if (childData) linkedChildren.push(childData);
      }

      // If we detect stale IDs in the parent doc, log it (staff can reconcile).
      const parentListIds = Array.isArray((parent as any).studentIds) ? ((parent as any).studentIds as any[]).map(String) : [];
      const resolvedIds = new Set(children.map(c => String(c.id)));
      const stale = parentListIds.filter(id => id && !resolvedIds.has(String(id)));
      if (stale.length > 0) {
        console.warn('[ParentService] Parent has studentIds that are not accessible via parentIds linkage (stale or missing reverse-link).', {
          parentId,
          staleStudentIds: stale,
        });
      }

      // Get comprehensive login statistics (never block dashboard)
      let loginStats: ParentLoginStats;
      try {
        loginStats = await ParentLoginTracker.getParentLoginStats(parentId);
      } catch (e) {
        console.warn('[ParentService] Login stats failed (non-fatal).', { parentId, error: this.describeFsError(e) });
        loginStats = {
          totalLogins: 0,
          totalTimeSpent: 0,
          averageSessionDuration: 0,
          sessionsThisWeek: 0,
          sessionsThisMonth: 0,
        };
      }

      console.debug('[ParentService] Compiled parent dashboard data', {
        parentId,
        children: linkedChildren.map(c => ({
          childId: c.child.id,
          squadId: c.squad?.id,
          hasSchedule: !!c.squad?.schedule,
          schedule: c.squad?.schedule
        }))
      });

      return {
        parent,
        linkedChildren,
        loginStats
      };
    } catch (error) {
      console.error('Error fetching parent dashboard data:', error);
      return null;
    }
  }

  /**
   * Get detailed data for a specific linked child including squad and teacher info
   */
  static async getLinkedChildData(childId: string): Promise<LinkedChildData | null> {
    try {
      // Get child user data
      const childDoc = await getDoc(doc(db, 'users', childId));
      if (!childDoc.exists()) {
        console.error('Child not found:', childId);
        return null;
      }

      const child = { id: childDoc.id, ...childDoc.data() } as User;
      return await this.getLinkedChildDataFromUser(child);
    } catch (error) {
      console.error('Error fetching child data:', error);
      return null;
    }
  }

  /**
   * Get detailed parent login history for admin purposes
   */
  static async getParentLoginHistory(parentId: string, limit?: number) {
    return await ParentLoginTracker.getParentLoginHistory(parentId, limit);
  }

  /**
   * Launch a student account by creating anonymous session
   */
  static async launchStudentAccount(childId: string, childName: string, squadId?: string): Promise<void> {
    try {
      // IMPORTANT (Safari): window.open MUST happen synchronously off the user click.
      // Do not await anything before opening the new window.
      const newWindow = window.open('', '_blank');
      if (!newWindow) {
        void window.appAlert('Please allow popups to launch the student account.');
        return;
      }

      // Set up the new window with loading state immediately
      newWindow.document.write(`
        <html>
          <head>
            <title>Launching ${childName}'s Account...</title>
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <style>
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100vh;
                margin: 0;
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                color: white;
                text-align: center;
                padding: 24px;
              }
              .loading { animation: spin 1s linear infinite; display: inline-block; }
              @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
              .card { max-width: 520px; }
              .hint { opacity: 0.9; font-size: 14px; margin-top: 12px; }
            </style>
          </head>
          <body>
            <div class="card">
              <div class="loading" style="font-size: 48px; margin-bottom: 20px;">🎵</div>
              <h2>Launching ${childName}'s Account...</h2>
              <p>Please wait while we set up the student session.</p>
              <div class="hint">If this window stays blank, ensure popups are allowed for this site.</div>
            </div>
          </body>
        </html>
      `);

      // If no squadId provided, try to find it from the child's squad
      let targetSquadId = squadId;
      
      if (!targetSquadId) {
        const childData = await this.getLinkedChildData(childId);
        if (childData?.squad?.id) {
          targetSquadId = childData.squad.id;
        } else {
          console.error('Cannot launch student account: No squad found for child', childId);
          void window.appAlert('Unable to launch student account. No class found for this student.');
          try {
            newWindow.close();
          } catch {
            // ignore
          }
          return;
        }
      }

      // Redirect the popup to a dedicated launcher route that authenticates the student
      // using popup-scoped session persistence (so we don't replace the parent session).
      const launchUrl =
        `/student-launch?childId=${encodeURIComponent(childId)}` +
        `&squadId=${encodeURIComponent(targetSquadId)}` +
        `&childName=${encodeURIComponent(childName)}`;
      newWindow.location.href = launchUrl;
      
    } catch (error) {
      console.error('Error launching student account:', error);
      void window.appAlert(`Failed to launch ${childName}'s account. Please try again.`);
    }
  }

  /**
   * Generate program dates based on squad schedule
   */
  static generateProgramDates(squad: Squad): string[] {
    if (squad.schedule?.mainDate) return [squad.schedule.mainDate];
    if (!squad.schedule || !squad.schedule.startDate || !squad.schedule.endDate) {
      return [];
    }

    // Helpers
    const toISO = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const parseISO = (s: string) => {
      // Ensure consistent parsing for YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(`${s}T00:00:00`);
      return new Date(s);
    };
    const normalizeDayToIndex = (val?: string): number | undefined => {
      if (!val) return undefined;
      const v = val.trim().toLowerCase();
      const map: Record<string, number> = {
        sunday: 0, sun: 0, sundays: 0,
        monday: 1, mon: 1, mondays: 1, mons: 1,
        tuesday: 2, tue: 2, tues: 2, tuesdays: 2,
        wednesday: 3, wed: 3, wednesdays: 3,
        thursday: 4, thu: 4, thur: 4, thurs: 4, thursdays: 4,
        friday: 5, fri: 5, fridays: 5,
        saturday: 6, sat: 6, saturdays: 6,
      };
      // handle strings like "MON 3:20 - 4:20 CT" by taking first token
      // also normalize tokens like "MONs" -> "mon"
      let token = v.split(/\s+/)[0];
      token = token.replace(/[^a-z]/g, '');
      if (token.endsWith('s') && !map[token]) {
        token = token.slice(0, -1);
      }
      return map[token];
    };

    const start = parseISO(squad.schedule.startDate);
    const end = parseISO(squad.schedule.endDate);
    const targetDay = normalizeDayToIndex(squad.schedule.dayOfWeek);
    if (targetDay === undefined) return [];

    // Build exclusion and inclusion sets (ISO date strings)
    const exceptions = new Set<string>([
      ...(squad.schedule.exceptions || []),
      ...(squad.schedule.inheritedExceptions || []),
    ].map(d => toISO(parseISO(d))));
    const makeups = new Set<string>((squad.schedule.makeups || []).map(d => toISO(parseISO(d))));

    // Generate weekly dates from start to end on the target weekday
    const resultISO: Set<string> = new Set();
    let current = new Date(start);
    // Move to the first occurrence
    while (current.getDay() !== targetDay && current <= end) {
      current.setDate(current.getDate() + 1);
    }
    while (current <= end) {
      const iso = toISO(current);
      if (!exceptions.has(iso)) {
        resultISO.add(iso);
      }
      current.setDate(current.getDate() + 7);
    }
    // Add makeups
    for (const iso of makeups) {
      resultISO.add(iso);
    }

    // Sort ascending and format for display
    const sorted = Array.from(resultISO)
      .map(s => parseISO(s))
      .sort((a, b) => a.getTime() - b.getTime());

    return sorted.map(d => d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }));
  }

  /**
   * Format a day-of-week token (e.g., "MON", "MONs", "Thurs", "Wednesday")
   * into a full day name, optionally pluralized (e.g., "Mondays").
   */
  static formatDayOfWeek(input?: string, opts: { plural?: boolean } = { plural: true }): string {
    if (!input) return '';
    const v = input.trim().toLowerCase();
    // normalize token
    let token = v.split(/\s+/)[0];
    token = token.replace(/[^a-z]/g, '');
    const map: Record<string, number> = {
      sunday: 0, sun: 0, sundays: 0,
      monday: 1, mon: 1, mondays: 1, mons: 1,
      tuesday: 2, tue: 2, tues: 2, tuesdays: 2,
      wednesday: 3, wed: 3, wednesdays: 3,
      thursday: 4, thu: 4, thur: 4, thurs: 4, thursdays: 4,
      friday: 5, fri: 5, fridays: 5,
      saturday: 6, sat: 6, saturdays: 6,
    };
    if (token.endsWith('s') && !(token in map)) token = token.slice(0, -1);
    const idx = map[token];
    const names = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    if (idx === undefined) return input; // fallback to original
    const name = names[idx];
    return opts.plural ? `${name}s` : name;
  }

  /**
   * Get formatted list of no-class dates (exceptions) for display
   */
  static getNoClassDates(squad: Squad): string[] {
    const allExceptions = Array.from(new Set([
      ...(squad.schedule?.exceptions || []),
      ...(squad.schedule?.inheritedExceptions || []),
    ].filter(Boolean)));
    if (allExceptions.length === 0) {
      return [];
    }
    const parseISO = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00`) : new Date(s);
    return allExceptions
      .map(d => parseISO(d))
      .sort((a, b) => a.getTime() - b.getTime())
      .map(d => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
  }

  /**
   * Format schedule time for display
   */
  static formatScheduleTime(startTime?: string, endTime?: string): string {
    if (!startTime) return 'Time TBD';

    const formatTime = (time: string) => {
      try {
        const [hours, minutes] = time.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${minutes} ${ampm}`;
      } catch {
        return time;
      }
    };

    if (endTime) {
      return `${formatTime(startTime)} - ${formatTime(endTime)}`;
    }

    return formatTime(startTime);
  }
}
